import React, { Component } from 'react';
import { toastr } from 'react-redux-toastr';
import { api } from '../../../config';

const MIN_WIDTH = 300;
const MIN_HEIGHT = 200;
const MAX_WIDTH = 1000;
const MAX_HEIGHT = 500;
const RATIO = 2;
const RATIO_TOLERANCE = 0.05;

/**
 * Validates image dimensions client-side before upload.
 * Resolves with { width, height } on pass, rejects with Error on fail.
 */
function validateImage(file) {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    const objectUrl = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      const { naturalWidth: w, naturalHeight: h } = img;

      if (w < MIN_WIDTH || h < MIN_HEIGHT) {
        return reject(new Error(`Ukuran terlalu kecil. Minimal ${MIN_WIDTH}x${MIN_HEIGHT}px. Gambar kamu: ${w}x${h}px.`));
      }
      if (w > MAX_WIDTH || h > MAX_HEIGHT) {
        return reject(new Error(`Ukuran terlalu besar. Maksimal ${MAX_WIDTH}x${MAX_HEIGHT}px. Gambar kamu: ${w}x${h}px.`));
      }
      const ratio = w / h;
      if (Math.abs(ratio - RATIO) > RATIO_TOLERANCE) {
        return reject(new Error(`Rasio harus 2:1 (contoh: 1000x500). Rasio gambar kamu: ${ratio.toFixed(2)}:1.`));
      }
      resolve({ width: w, height: h });
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Gagal membaca gambar.'));
    };
    img.src = objectUrl;
  });
}

class NotificationBannerUpload extends Component {
  constructor(props) {
    super(props);
    this.state = { uploading: false, preview: props.value || null, dragOver: false };
    this.inputRef = React.createRef();
    this.handleFileChange = this.handleFileChange.bind(this);
    this.handleRemove = this.handleRemove.bind(this);
    this.handleDragOver = this.handleDragOver.bind(this);
    this.handleDragLeave = this.handleDragLeave.bind(this);
    this.handleDrop = this.handleDrop.bind(this);
    this.handlePaste = this.handlePaste.bind(this);
  }

  componentDidMount() {
    document.addEventListener('paste', this.handlePaste);
  }

  componentWillUnmount() {
    document.removeEventListener('paste', this.handlePaste);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.value !== this.props.value && !this.props.value) {
      this.setState({ preview: null });
    }
  }

  handlePaste(e) {
    if (this.state.uploading || this.props.disabled || this.state.preview) return;
    const items = e.clipboardData && e.clipboardData.items;
    if (!items) return;
    for (let i = 0; i < items.length; i++) {
      if (items[i].kind === 'file' && items[i].type.startsWith('image/')) {
        const file = items[i].getAsFile();
        if (file) this.processFile(file);
        break;
      }
    }
  }

  handleDragOver(e) {
    e.preventDefault();
    if (!this.props.disabled && !this.state.uploading) {
      this.setState({ dragOver: true });
    }
  }

  handleDragLeave(e) {
    e.preventDefault();
    this.setState({ dragOver: false });
  }

  handleDrop(e) {
    e.preventDefault();
    this.setState({ dragOver: false });
    if (this.props.disabled || this.state.uploading) return;
    const file = e.dataTransfer.files && e.dataTransfer.files[0];
    if (file) this.processFile(file);
  }

  async handleFileChange(e) {
    const file = e.target.files && e.target.files[0];
    if (!e.target.files) return;
    e.target.value = '';
    if (!file) return;
    this.processFile(file);
  }

  async processFile(file) {
    if (!['image/jpeg', 'image/jpg', 'image/png'].includes(file.type)) {
      toastr.error('Format tidak valid', 'Gunakan file JPEG atau PNG.');
      return;
    }

    try {
      await validateImage(file);
    } catch (err) {
      toastr.error('Gambar ditolak', err.message);
      return;
    }

    this.setState({ uploading: true });
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await fetch(`${api.apiEndpoint}/uploadNotificationBannerImage`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });
      const data = await res.json();

      if (data.status === 200 && data.fileName) {
        const imageUrl = `${api.apiEndpoint}/images/notification-banner/${data.fileName}`;
        this.setState({ preview: imageUrl });
        this.props.onChange && this.props.onChange(imageUrl);
      } else {
        toastr.error('Upload gagal', data.errorMessage || 'Terjadi kesalahan saat upload.');
      }
    } catch (err) {
      toastr.error('Upload gagal', err.message || 'Terjadi kesalahan.');
    } finally {
      this.setState({ uploading: false });
    }
  }

  handleRemove() {
    this.setState({ preview: null });
    this.props.onChange && this.props.onChange(null);
  }

  render() {
    const { uploading, preview, dragOver } = this.state;
    const { disabled } = this.props;

    return (
      <div>
        {preview ? (
          <div style={{ position: 'relative', display: 'inline-block', marginBottom: 8 }}>
            <img
              src={preview}
              alt="notification banner"
              style={{ width: '100%', maxWidth: 400, maxHeight: 200, objectFit: 'cover', borderRadius: 6, display: 'block' }}
            />
            {!disabled && (
              <button
                type="button"
                onClick={this.handleRemove}
                style={{
                  position: 'absolute', top: 4, right: 4,
                  background: 'rgba(0,0,0,0.6)', color: '#fff',
                  border: 'none', borderRadius: 4, padding: '2px 8px',
                  cursor: 'pointer', fontSize: 12,
                }}
              >
                Hapus
              </button>
            )}
          </div>
        ) : (
          <div
            onClick={() => !disabled && !uploading && this.inputRef.current && this.inputRef.current.click()}
            onDragOver={this.handleDragOver}
            onDragLeave={this.handleDragLeave}
            onDrop={this.handleDrop}
            style={{
              border: `2px dashed ${dragOver ? '#4a90e2' : '#ccc'}`,
              borderRadius: 8,
              padding: '20px 16px',
              textAlign: 'center',
              cursor: disabled ? 'not-allowed' : 'pointer',
              background: dragOver ? '#eef4fd' : '#fafafa',
              color: '#888',
              fontSize: 13,
              maxWidth: 400,
              transition: 'border-color 0.15s, background 0.15s',
            }}
          >
            {uploading ? 'Mengupload…' : (
              <>
                <div style={{ fontSize: 24, marginBottom: 4 }}>🖼</div>
                <div>Klik, drag &amp; drop, atau paste gambar banner</div>
                <div style={{ fontSize: 11, marginTop: 4, color: '#aaa' }}>
                  Min: 300×200px · Maks: 1000×500px · Rasio 2:1 · JPEG/PNG
                </div>
              </>
            )}
          </div>
        )}
        <input
          ref={this.inputRef}
          type="file"
          accept="image/jpeg,image/jpg,image/png"
          style={{ display: 'none' }}
          onChange={this.handleFileChange}
          disabled={disabled || uploading}
        />
      </div>
    );
  }
}

export default NotificationBannerUpload;
